import { io, Socket } from "socket.io-client";
import urls from "../global/constants/UrlConstants";

export type LeadRealtimePayload = {
  title?: string;
  body?: string;
  text?: string;
  type?: string;
  leadId?: string;
  userIds?: string[];
  data?: Record<string, unknown>;
  at?: string;
};

type TableRefreshListener = (payload: LeadRealtimePayload) => void;

const LEAD_REFRESH_TYPES =
  /leadassign|leadreassign|leadunassign|leadcreated|leadwon|leadlost|noteadded|reminderadded|leadmoved/i;
// Note: regular leadstage updates intentionally excluded — they caused All/My/Team
// tables to silently refresh every few seconds and leak peer stage changes.

let socket: Socket | null = null;
let joinedUserId: string | null = null;
const tableListeners = new Set<TableRefreshListener>();

function getSocket(): Socket {
  if (!socket) {
    socket = io(urls.url_prod, {
      transports: ["websocket", "polling"],
      autoConnect: false,
      reconnection: true,
      reconnectionAttempts: 12,
      reconnectionDelay: 1500,
    });

    socket.on("connect", () => {
      if (joinedUserId) {
        socket?.emit("join", { userId: joinedUserId });
      }
    });

    socket.on("lead_notification", (payload: LeadRealtimePayload) => {
      dispatchTableRefresh(payload);
    });

    socket.on("lead_assigned", (payload: LeadRealtimePayload) => {
      dispatchTableRefresh(payload || { type: "LeadAssigned" });
    });

    socket.on("receive_reminder", (payload: LeadRealtimePayload) => {
      dispatchTableRefresh({
        ...(payload || {}),
        type: (payload && payload.type) || "ReminderAdded",
        title: (payload && payload.title) || "Reminder",
      });
    });
  }
  return socket;
}

function dispatchTableRefresh(payload: LeadRealtimePayload) {
  const type = String(payload?.type || "");
  if (type && !LEAD_REFRESH_TYPES.test(type)) {
    // Still notify listeners — pages can decide; toast layer filters separately.
  }
  tableListeners.forEach((listener) => {
    try {
      listener(payload || {});
    } catch {
      // ignore listener errors
    }
  });
}

/** Connect + join the current user's room (call after login). */
export function connectLeadRealtime(userId: string) {
  const uid = String(userId || "").trim();
  if (!uid) return;
  joinedUserId = uid;
  const s = getSocket();
  if (!s.connected) s.connect();
  s.emit("join", { userId: uid });
}

export function disconnectLeadRealtime() {
  joinedUserId = null;
  if (socket?.connected) socket.disconnect();
}

/**
 * Subscribe lead list pages to silent table refresh events.
 * Returns unsubscribe.
 */
export function subscribeLeadTableRefresh(listener: TableRefreshListener) {
  tableListeners.add(listener);
  return () => {
    tableListeners.delete(listener);
  };
}

export function shouldRefreshLeadTable(payload: LeadRealtimePayload): boolean {
  const type = String(payload?.type || "");
  if (!type) return true;
  return LEAD_REFRESH_TYPES.test(type);
}

function uniqueIds(ids: string[]): string[] {
  const seen: Record<string, true> = {};
  const out: string[] = [];
  ids.forEach((id) => {
    if (!id || seen[id]) return;
    seen[id] = true;
    out.push(id);
  });
  return out;
}

export function parseNotificationUserIds(raw: unknown): string[] {
  if (raw == null || raw === "") return [];
  if (Array.isArray(raw)) {
    return uniqueIds(raw.map((id) => String(id || "").trim()).filter(Boolean));
  }
  const s = String(raw).trim();
  if (!s || s === "undefined" || s === "null") return [];
  try {
    const parsed = JSON.parse(s);
    if (Array.isArray(parsed)) {
      return uniqueIds(
        parsed.map((id) => String(id || "").trim()).filter(Boolean)
      );
    }
    if (parsed != null && parsed !== "") return [String(parsed).trim()];
  } catch {
    // not JSON
  }
  if (s.includes(",")) {
    return uniqueIds(s.split(",").map((id) => id.trim()).filter(Boolean));
  }
  return [s];
}

export function isReminderNotification(n: {
  type?: unknown;
  title?: unknown;
  text?: unknown;
}): boolean {
  return /reminder/i.test(
    `${n?.type || ""} ${n?.title || ""} ${n?.text || ""}`
  );
}

/** Bell / toast / popup: this user only. Reminders must not be shared with co-assignees. */
export function notificationBelongsToUser(
  n: {
    userIds?: unknown;
    userId?: unknown;
    type?: unknown;
    title?: unknown;
    text?: unknown;
  },
  userId: string
): boolean {
  const me = String(userId || "").trim();
  if (!me || !n) return false;
  const ids = parseNotificationUserIds(n.userIds);
  if (isReminderNotification(n)) {
    if (ids.length > 1) return false;
    if (ids.length === 1) return ids[0] === me;
    return String(n.userId || "").trim() === me;
  }
  if (ids.length) return ids.includes(me);
  return String(n.userId || "").trim() === me;
}

export function isLeadRealtimeForUser(
  payload: LeadRealtimePayload,
  userId: string
): boolean {
  return notificationBelongsToUser(
    {
      userIds: payload?.userIds,
      type: payload?.type,
      title: payload?.title,
      text: payload?.text,
    },
    userId
  );
}

export default getSocket;
